import React, { useEffect, useState } from "react";
import { X, Download, Check } from "lucide-react";
import api from "../lib/api";

/**
 * Poster template picker modal.
 *
 * Shown when the host clicks "Download QR" on the event dashboard.
 * Fetches the template catalog from /api/qr-poster/templates and lazily
 * loads a live preview PNG for each card. Clicking a card downloads the
 * chosen poster (A4 portrait, print-ready) via /api/events/{id}/qr-poster/{tid}.png.
 *
 * Props:
 *  - open: boolean
 *  - eventId: string
 *  - eventSlug: string (used for the downloaded filename)
 *  - baseUrl: string (public site URL — required so the QR encodes the full guest link)
 *  - onClose: () => void
 */
export default function QrPosterPicker({ open, eventId, eventSlug, baseUrl, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [previews, setPreviews] = useState({}); // { [id]: objectURL }
  const [busy, setBusy] = useState({}); // { [id]: true } for download-in-progress
  const [downloaded, setDownloaded] = useState({}); // { [id]: true } for done-checkmark

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/qr-poster/templates").then((r) => {
      if (cancelled) return;
      const t = r.data?.templates || [];
      setTemplates(t);
      // Fetch all previews in parallel. Each render is ~130 KB and takes
      // <200 ms server-side, so 8 parallel requests complete in ~1 s instead
      // of the 4-8 s of sequential fetching.
      t.forEach(async (tpl) => {
        try {
          const res = await api.get(
            `/events/${eventId}/qr-poster/${tpl.id}.png`,
            { responseType: "blob", params: { base_url: baseUrl, size: "preview" } },
          );
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          setPreviews((p) => ({ ...p, [tpl.id]: url }));
        } catch (e) {
          // Show a hint in the card so users know something went wrong.
          if (!cancelled) {
            setPreviews((p) => ({ ...p, [tpl.id]: "__error__" }));
          }
        }
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, eventId, baseUrl]);

  // Free blob URLs when we close the modal so we don't leak memory.
  useEffect(() => {
    if (open) return;
    Object.values(previews).forEach((u) => {
      if (u && u !== "__error__") URL.revokeObjectURL(u);
    });
    setPreviews({});
    setDownloaded({});
  }, [open]);

  const download = async (tpl) => {
    setBusy((b) => ({ ...b, [tpl.id]: true }));
    try {
      const res = await api.get(
        `/events/${eventId}/qr-poster/${tpl.id}.png`,
        { responseType: "blob", params: { base_url: baseUrl } },
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${eventSlug}-${tpl.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setDownloaded((d) => ({ ...d, [tpl.id]: true }));
    } catch (err) {
      alert(`Download failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy((b) => ({ ...b, [tpl.id]: false }));
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-navy-900/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
      data-testid="qr-poster-picker"
    >
      <div
        className="bg-cream-100 rounded-3xl w-full max-w-6xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-navy-700/10">
          <div>
            <p className="tracking-widest text-coral-400 text-xs font-bold uppercase">
              Choose a Poster
            </p>
            <h2 className="font-serif text-navy-700 text-3xl mt-1">
              Print-ready QR posters
            </h2>
            <p className="text-navy-700/60 text-sm mt-1">
              Pick a template — your event QR is baked in and the PNG downloads instantly.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="qr-poster-close-btn"
            className="text-navy-700/50 hover:text-navy-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {templates.length === 0 && (
            <div className="col-span-full text-center text-navy-700/60 py-10">
              Loading templates…
            </div>
          )}
          {templates.map((tpl) => {
            const previewUrl = previews[tpl.id];
            const isBusy = !!busy[tpl.id];
            const isDone = !!downloaded[tpl.id];
            return (
              <button
                key={tpl.id}
                onClick={() => download(tpl)}
                disabled={isBusy}
                data-testid={`qr-poster-card-${tpl.id}`}
                className="text-left bg-white rounded-2xl overflow-hidden border border-navy-700/8 hover:border-coral-300 hover:shadow-lg transition-all group disabled:opacity-70 disabled:cursor-wait"
              >
                <div className="aspect-[1240/1754] bg-cream-200 overflow-hidden">
                  {previewUrl === "__error__" ? (
                    <div className="w-full h-full flex items-center justify-center text-navy-700/40 text-xs px-4 text-center">
                      Preview unavailable — click PNG to download
                    </div>
                  ) : previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={`${tpl.label} preview`}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-navy-700/30 text-xs">
                      generating preview…
                    </div>
                  )}
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-navy-700 text-sm">{tpl.label}</div>
                    <div className="text-xs text-navy-700/50 mt-0.5">{tpl.category}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 ${
                    isDone
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-coral-100 text-coral-500 group-hover:bg-coral-300 group-hover:text-white"
                  }`}>
                    {isDone ? (<><Check className="w-3.5 h-3.5" /> Saved</>)
                      : isBusy ? "…"
                        : (<><Download className="w-3.5 h-3.5" /> PNG</>)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-6 border-t border-navy-700/10 text-center">
          <p className="text-xs text-navy-700/60">
            Posters are A4 portrait, 150 DPI — print at any size or share as-is.
          </p>
        </div>
      </div>
    </div>
  );
}
